import { useQuery } from '@tanstack/react-query'
import type { PoolCapacityView, ReservationView } from '@/shared/api/__generated__'
import { getCapacityOptions, getReservationOptions, listReservationsOptions } from './queries'

export interface CapacityQueryState {
  isPending: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

export interface CapacityPicture {
  // Квант ёмкости и время прогрева — глобальные числа инсталляции, не пула.
  cellSlots: number
  leadTimeS: number
  pools: PoolCapacityView[]
}

interface CapacityFilter {
  // Пустой пул — картина по всем пулам инсталляции.
  pool?: string
}

export function useCapacity(filter: CapacityFilter = {}): CapacityQueryState & CapacityPicture {
  const query = useQuery(getCapacityOptions(filter.pool ? { query: { pool: filter.pool } } : {}))
  return {
    cellSlots: query.data?.cell_slots ?? 0,
    leadTimeS: query.data?.lead_time_s ?? 0,
    pools: query.data?.pools ?? [],
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

interface ReservationFilter {
  pool?: string
  account?: string
  // active=true оставляет незакрытые заявки: терминальные заводятся навсегда,
  // и без фильтра доска со временем становится историей, а не картиной.
  activeOnly?: boolean
}

export function useReservations(
  filter: ReservationFilter = {},
): CapacityQueryState & { reservations: ReservationView[] } {
  const query = useQuery(
    listReservationsOptions({
      query: {
        ...(filter.pool ? { pool: filter.pool } : {}),
        ...(filter.account ? { account: filter.account } : {}),
        ...(filter.activeOnly ? { active: true } : {}),
      },
    }),
  )
  return {
    reservations: query.data?.data ?? [],
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}

export function useReservation(
  id: string,
): CapacityQueryState & { reservation: ReservationView | undefined } {
  const query = useQuery({
    ...getReservationOptions({ path: { id } }),
    enabled: id !== '',
  })
  return {
    reservation: query.data?.data,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  }
}
